import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Upload, FileJson, AlertCircle, CheckCircle2, XCircle, 
  ArrowLeft, Download, Info
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const ProprietairesImport = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('upload'); // 'upload', 'review', 'confirm', 'result'
  const [importData, setImportData] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  const [resolutions, setResolutions] = useState({});
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!Array.isArray(data)) {
        toast.error('Le fichier doit contenir un tableau de propriétaires');
        return;
      }

      setImportData(data);
      toast.success(`${data.length} enregistrements chargés`);
      
      // Automatically preview
      await handlePreview(data);
    } catch (error) {
      toast.error('Erreur de lecture du fichier: ' + error.message);
    }
  };

  const handlePreview = async (data) => {
    setLoading(true);
    try {
      const response = await api.post('/proprietaires/import/preview', data || importData);
      setPreviewReport(response.data);
      
      // Initialize resolutions: all new entries default to 'create_new'
      const initialResolutions = {};
      response.data.report.nouvelles.forEach(item => {
        initialResolutions[item.index] = 'create_new';
      });
      response.data.report.doublons_possibles.forEach(item => {
        initialResolutions[item.index] = 'skip'; // Default to skip for duplicates
      });
      setResolutions(initialResolutions);
      
      setStep('review');
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur lors de l\'analyse: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    setLoading(true);
    try {
      const payload = {
        proprietaires: importData,
        resolutions: resolutions
      };
      
      const response = await api.post('/proprietaires/import/confirm', payload);
      setImportResult(response.data);
      setStep('result');
      toast.success(`Import terminé: ${response.data.summary.created} créé(s), ${response.data.summary.updated} mis à jour`);
    } catch (error) {
      toast.error('Erreur lors de l\'import: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleResolutionChange = (index, value) => {
    setResolutions(prev => ({ ...prev, [index]: value }));
  };

  const load15CompaniesData = async () => {
    setLoading(true);
    try {
      // Load the prepared 15 companies JSON file
      const response = await fetch('/15_companies_import.json');
      const data = await response.json();
      setImportData(data);
      toast.success(`15 sociétés hautement qualifiées chargées`);
      
      // Automatically preview
      await handlePreview(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des sociétés: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Cette page permet d'importer des propriétaires de véhicules (sociétés de transport) 
          à partir d'un fichier JSON. Le système détectera automatiquement les doublons potentiels.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Option 1: Importer les 15 sociétés recommandées</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Importer les 15 sociétés de transport hautement qualifiées identifiées lors de l'analyse.
          </p>
          <Button onClick={load15CompaniesData} disabled={loading} className="w-full">
            <Download className="mr-2 h-4 w-4" />
            Charger les 15 sociétés recommandées
          </Button>
        </CardContent>
      </Card>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Ou</span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Option 2: Importer un fichier personnalisé</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <Label htmlFor="file-upload" className="cursor-pointer">
              <span className="text-blue-600 hover:text-blue-500">Cliquez pour sélectionner un fichier</span>
              <span className="text-gray-500"> ou glissez-déposez</span>
            </Label>
            <Input
              id="file-upload"
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />
            <p className="text-xs text-gray-500 mt-2">Format JSON uniquement</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <h4 className="font-medium text-sm mb-2 flex items-center">
              <FileJson className="h-4 w-4 mr-2" />
              Format attendu
            </h4>
            <pre className="text-xs bg-white p-2 rounded overflow-x-auto">
{`[
  {
    "nom": "Nom de la société",
    "pays": "Côte d'Ivoire",
    "type_proprietaire": "Entreprise ou société de transport",
    "telephone": "0700000000",
    "telephone_2": "0700000001",
    "email": "contact@example.com",
    "adresse": "Adresse complète",
    "commentaire": "Informations complémentaires"
  }
]`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderReviewStep = () => {
    if (!previewReport) return null;

    const { summary, report } = previewReport;

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Résumé de l'analyse</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{summary.total}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{summary.nouvelles}</div>
                <div className="text-sm text-muted-foreground">Nouvelles</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{summary.doublons_possibles}</div>
                <div className="text-sm text-muted-foreground">Doublons possibles</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{summary.erreurs}</div>
                <div className="text-sm text-muted-foreground">Erreurs</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nouvelles entrées */}
        {report.nouvelles.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                Nouvelles entrées ({report.nouvelles.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.nouvelles.map((item) => (
                    <TableRow key={item.index}>
                      <TableCell className="font-medium">{item.data.nom}</TableCell>
                      <TableCell>{item.data.telephone}</TableCell>
                      <TableCell>{item.data.adresse}</TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-800">Créer</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Doublons possibles */}
        {report.doublons_possibles.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                Doublons possibles ({report.doublons_possibles.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {report.doublons_possibles.map((item) => (
                <div key={item.index} className="border rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Nouvelle entrée</h4>
                      <div className="text-sm space-y-1">
                        <div><strong>Nom:</strong> {item.nouvelle_data.nom}</div>
                        <div><strong>Tél:</strong> {item.nouvelle_data.telephone}</div>
                        <div><strong>Adresse:</strong> {item.nouvelle_data.adresse}</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Existant (Matricule: {item.existant.matricule})</h4>
                      <div className="text-sm space-y-1">
                        <div><strong>Nom:</strong> {item.existant.nom}</div>
                        <div><strong>Tél:</strong> {item.existant.telephone}</div>
                        <div><strong>Adresse:</strong> {item.existant.adresse}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                    <p className="text-xs text-yellow-800">
                      <strong>Raison:</strong> {item.raison}
                    </p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Que souhaitez-vous faire ?</Label>
                    <RadioGroup
                      value={resolutions[item.index] || 'skip'}
                      onValueChange={(value) => handleResolutionChange(item.index, value)}
                      className="mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="skip" id={`skip-${item.index}`} />
                        <Label htmlFor={`skip-${item.index}`} className="cursor-pointer">
                          Ignorer (garder l'existant)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="update" id={`update-${item.index}`} />
                        <Label htmlFor={`update-${item.index}`} className="cursor-pointer">
                          Mettre à jour l'existant
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="create_new" id={`create-${item.index}`} />
                        <Label htmlFor={`create-${item.index}`} className="cursor-pointer">
                          Créer comme nouvelle entrée
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Erreurs */}
        {report.erreurs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <XCircle className="h-5 w-5 text-red-600 mr-2" />
                Erreurs ({report.erreurs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Index</TableHead>
                    <TableHead>Données</TableHead>
                    <TableHead>Erreur</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.erreurs.map((item) => (
                    <TableRow key={item.index}>
                      <TableCell>{item.index}</TableCell>
                      <TableCell className="font-mono text-xs">{JSON.stringify(item.data).substring(0, 50)}...</TableCell>
                      <TableCell className="text-red-600">{item.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setStep('upload')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <Button 
            onClick={handleConfirmImport} 
            disabled={loading || (summary.nouvelles === 0 && summary.doublons_possibles === 0)}
          >
            Confirmer l'import
          </Button>
        </div>
      </div>
    );
  };

  const renderResultStep = () => {
    if (!importResult) return null;

    const { summary, details } = importResult;

    return (
      <div className="space-y-6">
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Import terminé avec succès !
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle>Résumé de l'import</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{summary.created}</div>
                <div className="text-sm text-muted-foreground">Créés</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{summary.updated}</div>
                <div className="text-sm text-muted-foreground">Mis à jour</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{summary.skipped}</div>
                <div className="text-sm text-muted-foreground">Ignorés</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{summary.errors}</div>
                <div className="text-sm text-muted-foreground">Erreurs</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Créés */}
        {details.created.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                Propriétaires créés ({details.created.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Matricule</TableHead>
                    <TableHead>Nom</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {details.created.map((item) => (
                    <TableRow key={item.index}>
                      <TableCell className="font-mono text-sm">{item.matricule}</TableCell>
                      <TableCell>{item.nom}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Mis à jour */}
        {details.updated.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle2 className="h-5 w-5 text-blue-600 mr-2" />
                Propriétaires mis à jour ({details.updated.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Matricule</TableHead>
                    <TableHead>Nom</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {details.updated.map((item) => (
                    <TableRow key={item.index}>
                      <TableCell className="font-mono text-sm">{item.matricule}</TableCell>
                      <TableCell>{item.nom}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/proprietaires')}>
            Voir tous les propriétaires
          </Button>
          <Button onClick={() => {
            setStep('upload');
            setImportData(null);
            setPreviewReport(null);
            setResolutions({});
            setImportResult(null);
          }}>
            Nouvel import
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Import de Propriétaires</h1>
          <p className="text-muted-foreground mt-1">
            Importez des sociétés de transport en tant que propriétaires de véhicules
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/proprietaires')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour aux propriétaires
        </Button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center space-x-4">
        <div className={`flex items-center ${step === 'upload' ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
            step === 'upload' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
          }`}>
            1
          </div>
          <span className="ml-2 font-medium">Upload</span>
        </div>
        <div className="w-16 h-0.5 bg-gray-300" />
        <div className={`flex items-center ${step === 'review' ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
            step === 'review' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
          }`}>
            2
          </div>
          <span className="ml-2 font-medium">Révision</span>
        </div>
        <div className="w-16 h-0.5 bg-gray-300" />
        <div className={`flex items-center ${step === 'result' ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
            step === 'result' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
          }`}>
            3
          </div>
          <span className="ml-2 font-medium">Résultat</span>
        </div>
      </div>

      {/* Content */}
      {step === 'upload' && renderUploadStep()}
      {step === 'review' && renderReviewStep()}
      {step === 'result' && renderResultStep()}
    </div>
  );
};

export default ProprietairesImport;
